// src/pages/UserManagement.jsx
import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';

// Available roles for assignment
const AVAILABLE_ROLES = [
  // Executive Committee
  { id: 'president', name: 'President', level: 95, category: 'Executive', icon: 'fa-crown', color: '#ffd700' },
  { id: 'vicePresident', name: 'Vice President', level: 90, category: 'Executive', icon: 'fa-user-tie', color: '#cce5ff' },
  { id: 'secretary', name: 'Secretary', level: 85, category: 'Executive', icon: 'fa-file-alt', color: '#cce5ff' },
  { id: 'treasurer', name: 'Treasurer', level: 85, category: 'Executive', icon: 'fa-coins', color: '#cce5ff' },
  
  // Committee Members
  { id: 'sportsMember', name: 'Sports Member', level: 60, category: 'Committee', icon: 'fa-futbol', color: '#d4edda' },
  { id: 'culturalMember', name: 'Cultural Member', level: 60, category: 'Committee', icon: 'fa-music', color: '#d4edda' },
  { id: 'technicalMember', name: 'Technical Member', level: 60, category: 'Committee', icon: 'fa-microchip', color: '#d4edda' },
  { id: 'socialMember', name: 'Social Member', level: 60, category: 'Committee', icon: 'fa-glass-cheers', color: '#d4edda' },
  { id: 'generalMember', name: 'General Member', level: 50, category: 'Committee', icon: 'fa-users', color: '#d4edda' },
  
  // Staff
  { id: 'clubManager', name: 'Club Manager', level: 75, category: 'Staff', icon: 'fa-building', color: '#fff3cd' },
  { id: 'clubAdministrator', name: 'Club Administrator', level: 70, category: 'Staff', icon: 'fa-clipboard-list', color: '#fff3cd' },
  
  // Regular
  { id: 'member', name: 'Regular Member', level: 10, category: 'Regular', icon: 'fa-user', color: '#e2e3e5' },
  
  // Super Admin
  { id: 'admin', name: 'Super Administrator', level: 100, category: 'Executive', icon: 'fa-shield-alt', color: '#ffd700' }
];

const UserManagement = ({ 
  users: propUsers = [], 
  onUpdateUser, 
  onDeleteUser, 
  onActivateUser, 
  onSuspendUser,
  onAssignRole,
  addNotification,
  currentUserRole = 'admin',
  canAssignRoles = true
}) => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterRole, setFilterRole] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterCategory, setFilterCategory] = useState('all');
  const [selectedUser, setSelectedUser] = useState(null);
  const [showRoleModal, setShowRoleModal] = useState(false);
  const [selectedRole, setSelectedRole] = useState('');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [showSuspendModal, setShowSuspendModal] = useState(false);
  const [userToSuspend, setUserToSuspend] = useState(null);
  const [suspendReason, setSuspendReason] = useState('');

  // Debug log
  console.log('UserManagement - propUsers received:', propUsers);
  console.log('UserManagement - propUsers length:', propUsers?.length);

  // Use prop users if provided
  useEffect(() => {
    if (propUsers && Array.isArray(propUsers)) {
      setUsers(propUsers);
    } else {
      setUsers([]);
    }
  }, [propUsers]);

  const handleAssignRole = async () => {
    if (selectedUser && selectedRole && onAssignRole) {
      await onAssignRole(selectedUser.id, selectedRole);
      setShowRoleModal(false);
      setSelectedUser(null);
      setSelectedRole('');
    }
  };

  const openRoleModal = (user) => {
    setSelectedUser(user);
    setSelectedRole(user.account_type || 'member');
    setShowRoleModal(true);
  };

  const openDeleteConfirm = (user) => {
    setUserToDelete(user);
    setShowDeleteConfirm(true);
  };

  const handleDeleteConfirm = async () => {
    if (userToDelete && onDeleteUser) {
      await onDeleteUser(userToDelete.id);
      setShowDeleteConfirm(false);
      setUserToDelete(null);
    }
  };

  const openSuspendModal = (user) => {
    setUserToSuspend(user);
    setSuspendReason('');
    setShowSuspendModal(true);
  };

  const handleSuspendConfirm = async () => {
    if (userToSuspend && onSuspendUser) {
      await onSuspendUser(userToSuspend.id, suspendReason);
      setShowSuspendModal(false);
      setUserToSuspend(null);
      setSuspendReason('');
    }
  };

  // Get role display name
  const getRoleDisplayName = (roleId) => {
    const role = AVAILABLE_ROLES.find(r => r.id === roleId);
    return role ? role.name : roleId || 'Member';
  };

  // Get role icon
  const getRoleIcon = (roleId) => {
    const role = AVAILABLE_ROLES.find(r => r.id === roleId);
    return role ? role.icon : 'fa-user';
  };

  // Get role color class
  const getRoleColorClass = (roleId) => {
    const role = AVAILABLE_ROLES.find(r => r.id === roleId);
    if (!role) return 'role-regular';
    
    switch (role.category) {
      case 'Executive': return 'role-executive';
      case 'Committee': return 'role-committee';
      case 'Staff': return 'role-staff';
      default: return 'role-regular';
    }
  };

  // Filter users
  const filteredUsers = (users || []).filter(user => {
    if (!user) return false;
    if (filterStatus !== 'all' && user.status !== filterStatus) return false;
    if (filterRole !== 'all' && user.account_type !== filterRole) return false;
    if (filterCategory !== 'all') {
      const roleInfo = AVAILABLE_ROLES.find(r => r.id === user.account_type);
      if (!roleInfo) return false;
      if (roleInfo.category !== filterCategory) return false;
    }
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      const fullName = `${user.first_name || ''} ${user.last_name || ''}`.toLowerCase();
      return fullName.includes(term) || (user.email || '').toLowerCase().includes(term);
    }
    return true;
  });

  // Group users by category
  const executiveUsers = filteredUsers.filter(u => {
    const role = AVAILABLE_ROLES.find(r => r.id === u.account_type);
    return role?.category === 'Executive';
  });
  
  const committeeUsers = filteredUsers.filter(u => {
    const role = AVAILABLE_ROLES.find(r => r.id === u.account_type);
    return role?.category === 'Committee';
  });
  
  const staffUsers = filteredUsers.filter(u => {
    const role = AVAILABLE_ROLES.find(r => r.id === u.account_type);
    return role?.category === 'Staff';
  });
  
  const regularUsers = filteredUsers.filter(u => {
    const role = AVAILABLE_ROLES.find(r => r.id === u.account_type);
    return !role || role?.category === 'Regular';
  });

  const getStatusBadgeClass = (status) => {
    switch (status) {
      case 'active': return 'status-active';
      case 'pending': return 'status-pending';
      case 'suspended': return 'status-suspended';
      case 'inactive': return 'status-inactive';
      default: return 'status-unknown';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'active': return 'fa-check-circle';
      case 'pending': return 'fa-clock';
      case 'suspended': return 'fa-ban';
      case 'inactive': return 'fa-circle';
      default: return 'fa-question-circle';
    }
  };

  // Render user table by category
  const renderUserTable = (userList, title, icon, categoryColor) => {
    if (userList.length === 0) return null;
    
    return (
      <div className="user-category-section">
        <div className="category-header" style={{ borderLeftColor: categoryColor }}>
          <i className={`fas ${icon}`} style={{ color: categoryColor }}></i>
          <h4>{title}</h4>
          <span className="category-count" style={{ backgroundColor: categoryColor }}>{userList.length}</span>
        </div>
        <div className="users-table-container">
          <table className="users-table">
            <thead>
              <tr>
                <th>User</th>
                <th>Email</th>
                <th>Membership #</th>
                <th>Role</th>
                <th>Status</th>
                <th>Joined</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {userList.map((user) => (
                <tr key={user.id}>
                  <td>
                    <div className="user-cell">
                      <div className="user-avatar" style={{ backgroundColor: categoryColor + '20', color: categoryColor }}>
                        <i className="fas fa-user-circle"></i>
                      </div>
                      <div className="user-info">
                        <strong>{user.first_name} {user.last_name}</strong>
                        <small>{user.membership_number}</small>
                      </div>
                    </div>
                  </td>
                  <td>{user.email}</td>
                  <td>{user.membership_number}</td>
                  <td>
                    <span className={`role-badge ${getRoleColorClass(user.account_type)}`}>
                      <i className={`fas ${getRoleIcon(user.account_type)}`}></i>
                      {getRoleDisplayName(user.account_type)}
                    </span>
                  </td>
                  <td>
                    <span className={`status-badge ${getStatusBadgeClass(user.status)}`}>
                      <i className={`fas ${getStatusIcon(user.status)}`}></i>
                      {user.status}
                    </span>
                  </td>
                  <td>{new Date(user.created_at).toLocaleDateString()}</td>
                  <td>
                    <div className="action-buttons">
                      <button 
                        className="action-btn view" 
                        title="View Details"
                        onClick={() => onUpdateUser && onUpdateUser(user.id)}
                      >
                        <i className="fas fa-eye"></i>
                      </button>
                      {canAssignRoles && (
                        <button 
                          className="action-btn role" 
                          title="Assign Role"
                          onClick={() => openRoleModal(user)}
                        >
                          <i className="fas fa-user-tag"></i>
                        </button>
                      )}
                      {user.status !== 'active' && (
                        <button 
                          className="action-btn activate" 
                          title="Activate"
                          onClick={() => onActivateUser && onActivateUser(user.id)}
                        >
                          <i className="fas fa-check-circle"></i>
                        </button>
                      )}
                      {user.status === 'active' && (
                        <button 
                          className="action-btn suspend" 
                          title="Suspend"
                          onClick={() => openSuspendModal(user)}
                        >
                          <i className="fas fa-ban"></i>
                        </button>
                      )}
                      <button 
                        className="action-btn delete" 
                        title="Delete"
                        onClick={() => openDeleteConfirm(user)}
                      >
                        <i className="fas fa-trash"></i>
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="spinner"></div>
        <p>Loading users...</p>
      </div>
    );
  }

  return (
    <div className="user-management">
      <div className="user-management-header">
        <h3><i className="fas fa-users-gear"></i> User Management</h3>
        <p>Manage system users, assign roles, and set permissions</p>
      </div>

      {/* Filters */}
      <div className="user-filters">
        <div className="search-box">
          <i className="fas fa-search"></i>
          <input
            type="text"
            placeholder="Search by name or email..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <select value={filterCategory} onChange={(e) => setFilterCategory(e.target.value)}>
          <option value="all">All Categories</option>
          <option value="Executive">Executive Committee</option>
          <option value="Committee">Committee Members</option>
          <option value="Staff">Club Staff</option>
          <option value="Regular">Regular Members</option>
        </select>
        <select value={filterRole} onChange={(e) => setFilterRole(e.target.value)}>
          <option value="all">All Roles</option>
          {AVAILABLE_ROLES.map(role => (
            <option key={role.id} value={role.id}>{role.name}</option>
          ))}
        </select>
        <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}>
          <option value="all">All Status</option>
          <option value="active">Active</option>
          <option value="pending">Pending</option>
          <option value="suspended">Suspended</option>
          <option value="inactive">Inactive</option>
        </select>
      </div>

      {/* Users by Category */}
      {filteredUsers.length === 0 ? (
        <div className="empty-state">
          <i className="fas fa-users-slash"></i>
          <h3>No Users Found</h3>
          <p>No users match your search criteria. Total users in system: {users.length}</p>
        </div>
      ) : (
        <>
          {renderUserTable(executiveUsers, 'Executive Committee', 'fa-crown', '#ffd700')}
          {renderUserTable(committeeUsers, 'Committee Members', 'fa-users', '#28a745')}
          {renderUserTable(staffUsers, 'Club Staff', 'fa-briefcase', '#17a2b8')}
          {renderUserTable(regularUsers, 'Regular Members', 'fa-user', '#6c757d')}
        </>
      )}

      {/* Role Assignment Modal */}
      {showRoleModal && selectedUser && (
        <div className="modal-overlay" onClick={() => setShowRoleModal(false)}>
          <div className="role-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>
                <i className="fas fa-user-tag"></i>
                Assign Role
              </h3>
              <button className="close-btn" onClick={() => setShowRoleModal(false)}>
                <i className="fas fa-times"></i>
              </button>
            </div>
            <div className="modal-body">
              <div className="user-info">
                <div className="user-avatar-large">
                  <i className="fas fa-user-circle"></i>
                </div>
                <div>
                  <p><strong>{selectedUser.first_name} {selectedUser.last_name}</strong></p>
                  <p><small>{selectedUser.email}</small></p>
                  <p><small>Current Role: <strong>{getRoleDisplayName(selectedUser.account_type)}</strong></small></p>
                </div>
              </div>
              
              <div className="form-group">
                <label>Select New Role:</label>
                <select 
                  value={selectedRole} 
                  onChange={(e) => setSelectedRole(e.target.value)}
                  className="role-select"
                >
                  {AVAILABLE_ROLES.map(role => (
                    <option key={role.id} value={role.id}>
                      {role.name} - {role.category}
                    </option>
                  ))}
                </select>
              </div>
              
              <div className="role-info">
                <i className="fas fa-info-circle"></i>
                <div>
                  <p><strong>Role Change Impact:</strong></p>
                  <ul>
                    <li>User's access permissions will be updated immediately</li>
                    <li>User will see different menu options based on new role</li>
                    <li>This change will be logged for audit purposes</li>
                  </ul>
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn-cancel" onClick={() => setShowRoleModal(false)}>
                Cancel
              </button>
              <button className="btn-save" onClick={handleAssignRole}>
                <i className="fas fa-save"></i> Assign Role
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && userToDelete && (
        <div className="modal-overlay" onClick={() => setShowDeleteConfirm(false)}>
          <div className="confirm-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>
                <i className="fas fa-trash"></i>
                Delete User
              </h3>
              <button className="close-btn" onClick={() => setShowDeleteConfirm(false)}>
                <i className="fas fa-times"></i>
              </button>
            </div>
            <div className="modal-body">
              <div className="warning-icon">
                <i className="fas fa-exclamation-triangle"></i>
              </div>
              <p>Are you sure you want to delete <strong>{userToDelete.first_name} {userToDelete.last_name}</strong>?</p>
              <p className="warning-text">This action cannot be undone. All user data will be permanently removed.</p>
            </div>
            <div className="modal-footer">
              <button className="btn-cancel" onClick={() => setShowDeleteConfirm(false)}>
                Cancel
              </button>
              <button className="btn-danger" onClick={handleDeleteConfirm}>
                <i className="fas fa-trash"></i> Delete User
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Suspend Confirmation Modal */}
      {showSuspendModal && userToSuspend && (
        <div className="modal-overlay" onClick={() => setShowSuspendModal(false)}>
          <div className="suspend-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>
                <i className="fas fa-ban"></i>
                Suspend User
              </h3>
              <button className="close-btn" onClick={() => setShowSuspendModal(false)}>
                <i className="fas fa-times"></i>
              </button>
            </div>
            <div className="modal-body">
              <p>Suspend <strong>{userToSuspend.first_name} {userToSuspend.last_name}</strong>?</p>
              
              <div className="form-group">
                <label>Reason for suspension:</label>
                <textarea
                  value={suspendReason}
                  onChange={(e) => setSuspendReason(e.target.value)}
                  placeholder="Enter reason for suspension..."
                  className="reason-textarea"
                  rows="3"
                />
              </div>
              
              <div className="warning-info">
                <i className="fas fa-info-circle"></i>
                <p>Suspended users cannot log in or make new bookings until reactivated.</p>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn-cancel" onClick={() => setShowSuspendModal(false)}>
                Cancel
              </button>
              <button className="btn-warning" onClick={handleSuspendConfirm} disabled={!suspendReason}>
                <i className="fas fa-ban"></i> Suspend User
              </button>
            </div>
          </div>
        </div>
      )}

      <style>{`
        .user-management {
          padding: 20px;
        }
        .user-management-header {
          margin-bottom: 24px;
        }
        .user-management-header h3 {
          margin: 0 0 4px 0;
          font-size: 20px;
          color: #1f2937;
        }
        .user-management-header p {
          margin: 0;
          color: #6b7280;
          font-size: 14px;
        }
        .user-category-section {
          margin-bottom: 32px;
          background: white;
          border-radius: 12px;
          overflow: hidden;
          box-shadow: 0 1px 3px rgba(0,0,0,0.1);
        }
        .category-header {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 16px 20px;
          background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
          border-left: 4px solid;
          border-bottom: 1px solid #dee2e6;
        }
        .category-header i {
          font-size: 20px;
        }
        .category-header h4 {
          margin: 0;
          font-size: 16px;
          font-weight: 600;
          color: #2c3e50;
        }
        .category-count {
          margin-left: auto;
          padding: 2px 10px;
          border-radius: 20px;
          font-size: 12px;
          font-weight: 600;
          color: white;
        }
        .role-badge {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          padding: 4px 10px;
          border-radius: 20px;
          font-size: 11px;
          font-weight: 600;
        }
        .role-executive {
          background: #ffd700;
          color: #856404;
        }
        .role-committee {
          background: #d4edda;
          color: #155724;
        }
        .role-staff {
          background: #fff3cd;
          color: #856404;
        }
        .role-regular {
          background: #e2e3e5;
          color: #383d41;
        }
        .status-badge {
          display: inline-flex;
          align-items: center;
          gap: 4px;
          padding: 4px 8px;
          border-radius: 20px;
          font-size: 11px;
          font-weight: 500;
        }
        .status-active {
          background: #d4edda;
          color: #155724;
        }
        .status-pending {
          background: #fff3cd;
          color: #856404;
        }
        .status-suspended {
          background: #f8d7da;
          color: #721c24;
        }
        .status-inactive {
          background: #e2e3e5;
          color: #383d41;
        }
        .action-btn {
          background: none;
          border: none;
          width: 32px;
          height: 32px;
          border-radius: 8px;
          cursor: pointer;
          transition: all 0.2s;
          color: #6c757d;
        }
        .action-btn:hover {
          background: #f8f9fa;
        }
        .action-btn.view:hover { color: #00843D; }
        .action-btn.activate:hover { color: #28a745; }
        .action-btn.suspend:hover { color: #ffc107; }
        .action-btn.delete:hover { color: #dc3545; }
        .action-btn.role:hover { color: #17a2b8; }
        .user-avatar {
          width: 40px;
          height: 40px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 24px;
        }
        .user-avatar-large {
          width: 60px;
          height: 60px;
          border-radius: 50%;
          background: #e8f5e9;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 36px;
          color: #00843D;
        }
        .modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0,0,0,0.5);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1000;
        }
        .role-modal, .confirm-modal, .suspend-modal {
          background: white;
          border-radius: 16px;
          width: 500px;
          max-width: 90%;
          max-height: 80vh;
          overflow-y: auto;
        }
        .modal-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 20px;
          border-bottom: 1px solid #dee2e6;
        }
        .modal-body {
          padding: 20px;
        }
        .modal-footer {
          padding: 20px;
          border-top: 1px solid #dee2e6;
          display: flex;
          justify-content: flex-end;
          gap: 12px;
        }
        .user-info {
          display: flex;
          gap: 16px;
          align-items: center;
          margin-bottom: 20px;
          padding: 16px;
          background: #f8f9fa;
          border-radius: 12px;
        }
        .form-group {
          margin-bottom: 16px;
        }
        .form-group label {
          display: block;
          margin-bottom: 8px;
          font-weight: 500;
        }
        .role-select {
          width: 100%;
          padding: 10px 12px;
          border: 1px solid #dee2e6;
          border-radius: 8px;
          font-size: 14px;
        }
        .role-info {
          background: #e8f5e9;
          padding: 16px;
          border-radius: 12px;
          display: flex;
          gap: 12px;
          margin-top: 20px;
        }
        .role-info i {
          color: #00843D;
          font-size: 20px;
        }
        .role-info ul {
          margin: 8px 0 0 20px;
          font-size: 13px;
        }
        .warning-icon {
          text-align: center;
          font-size: 48px;
          color: #dc3545;
          margin-bottom: 16px;
        }
        .warning-text {
          color: #dc3545;
          font-size: 13px;
          margin-top: 12px;
        }
        .warning-info {
          background: #fff3cd;
          padding: 12px;
          border-radius: 8px;
          display: flex;
          gap: 10px;
          font-size: 13px;
        }
        .reason-textarea {
          width: 100%;
          padding: 10px;
          border: 1px solid #dee2e6;
          border-radius: 8px;
          resize: vertical;
        }
        .btn-cancel {
          background: #f8f9fa;
          color: #6c757d;
          border: 1px solid #dee2e6;
          padding: 10px 20px;
          border-radius: 8px;
          cursor: pointer;
        }
        .btn-save {
          background: #00843D;
          color: white;
          border: none;
          padding: 10px 20px;
          border-radius: 8px;
          cursor: pointer;
        }
        .btn-danger {
          background: #dc3545;
          color: white;
          border: none;
          padding: 10px 20px;
          border-radius: 8px;
          cursor: pointer;
        }
        .btn-warning {
          background: #ffc107;
          color: #856404;
          border: none;
          padding: 10px 20px;
          border-radius: 8px;
          cursor: pointer;
        }
        .btn-save:hover, .btn-danger:hover, .btn-warning:hover {
          opacity: 0.9;
        }
        .user-filters {
          display: flex;
          gap: 12px;
          margin-bottom: 24px;
          flex-wrap: wrap;
        }
        .search-box {
          flex: 1;
          position: relative;
          min-width: 200px;
        }
        .search-box i {
          position: absolute;
          left: 12px;
          top: 50%;
          transform: translateY(-50%);
          color: #6c757d;
        }
        .search-box input {
          width: 100%;
          padding: 10px 12px 10px 36px;
          border: 1px solid #dee2e6;
          border-radius: 8px;
        }
        .user-filters select {
          padding: 10px 12px;
          border: 1px solid #dee2e6;
          border-radius: 8px;
          background: white;
        }
        .users-table-container {
          overflow-x: auto;
        }
        .users-table {
          width: 100%;
          border-collapse: collapse;
        }
        .users-table th {
          text-align: left;
          padding: 12px;
          background: #f8f9fa;
          font-weight: 600;
          border-bottom: 2px solid #dee2e6;
        }
        .users-table td {
          padding: 12px;
          border-bottom: 1px solid #f3f4f6;
          vertical-align: middle;
        }
        .user-cell {
          display: flex;
          align-items: center;
          gap: 12px;
        }
        .user-info {
          display: flex;
          flex-direction: column;
        }
        .user-info small {
          font-size: 11px;
          color: #6c757d;
        }
        .action-buttons {
          display: flex;
          gap: 4px;
        }
        .empty-state {
          text-align: center;
          padding: 60px 20px;
          color: #6c757d;
        }
        .empty-state i {
          font-size: 48px;
          margin-bottom: 16px;
        }
        .loading-container {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          padding: 60px 20px;
        }
        .spinner {
          border: 4px solid #f3f3f3;
          border-top: 4px solid #00843D;
          border-radius: 50%;
          width: 40px;
          height: 40px;
          animation: spin 1s linear infinite;
        }
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default UserManagement;